import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Avatar,
  AvatarFallback,
  Input,
  Label,
  Alert,
  AlertDescription,
  useToast,
} from "@repo/ui";
import {
  UserCircle,
  Key,
  Shield,
  UserCheck,
  Eye,
  Loader2,
  CheckCircle2,
  Pencil,
} from "lucide-react";
import { changePassword, updateProfile } from "@/lib/api/users";
import { validatePassword } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/auth-provider";
import { TwoFactorSection } from "./two-factor-section";
import type { ApiClientError } from "@/lib/api/client";

interface UserData {
  id: string;
  name: string;
  role: string;
  initials: string;
}

interface ProfileSectionProps {
  user: UserData;
  canEditSettings?: boolean;
}

const roleLabels: Record<string, string> = {
  admin: "Admin",
  supervising_pharmacist: "Supervising Pharmacist",
  pharmacist: "Pharmacist",
  technician: "Technician",
  auditor: "Auditor",
};

const roleDescriptions: Record<string, string> = {
  admin: "Full access to all features and settings",
  supervising_pharmacist: "Can override and manage most pharmacy operations",
  pharmacist: "Can create and view pharmacy records",
  technician: "Limited access for basic operations",
  auditor: "Read-only access for compliance review",
};

const roleColors: Record<
  string,
  "default" | "destructive" | "outline" | "secondary"
> = {
  admin: "destructive",
  supervising_pharmacist: "default",
  pharmacist: "secondary",
  technician: "outline",
  auditor: "outline",
};

const roleIcons: Record<string, React.ReactNode> = {
  admin: <Shield className="h-3.5 w-3.5" />,
  supervising_pharmacist: <UserCheck className="h-3.5 w-3.5" />,
  pharmacist: <UserCheck className="h-3.5 w-3.5" />,
  technician: <UserCheck className="h-3.5 w-3.5" />,
  auditor: <Eye className="h-3.5 w-3.5" />,
};

export function ProfileSection({ user }: ProfileSectionProps) {
  const { toast } = useToast();
  const auth = useAuth();

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(user.name);
  const [editInitials, setEditInitials] = useState(user.initials);
  const [profileError, setProfileError] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Password state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleStartEditing = () => {
    setEditName(user.name);
    setEditInitials(user.initials);
    setProfileError("");
    setIsEditingProfile(true);
  };

  const handleCancelEditing = () => {
    setIsEditingProfile(false);
    setProfileError("");
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError("");

    const trimmedName = editName.trim();
    const trimmedInitials = editInitials.trim().toUpperCase();

    if (!trimmedName) {
      setProfileError("Name is required");
      return;
    }

    if (trimmedInitials.length < 2 || trimmedInitials.length > 5) {
      setProfileError("Initials must be 2-5 characters");
      return;
    }

    if (!/^[A-Z]{2,5}$/.test(trimmedInitials)) {
      setProfileError("Initials must be uppercase letters only");
      return;
    }

    // Only send changed fields
    const payload: { fullName?: string; initials?: string } = {};
    if (trimmedName !== user.name) payload.fullName = trimmedName;
    if (trimmedInitials !== user.initials) payload.initials = trimmedInitials;

    if (Object.keys(payload).length === 0) {
      setIsEditingProfile(false);
      return;
    }

    setIsSavingProfile(true);
    try {
      await updateProfile(payload);
      await auth.refetch();
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
        variant: "success",
      });
      setIsEditingProfile(false);
    } catch (err) {
      const message =
        (err as ApiClientError).message || "Failed to update profile";
      setProfileError(message);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess(false);
  };

  const handleCancelPasswordChange = () => {
    resetPasswordForm();
    setShowPasswordForm(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (newPassword.length < 4 || newPassword.length > 16) {
      setPasswordError("Password must be 4-16 characters");
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError("New password must be different from current password");
      return;
    }

    setIsChangingPassword(true);

    try {
      await changePassword({ currentPassword, newPassword });

      // Re-validate session with new password
      await validatePassword({
        initials: user.initials,
        password: newPassword,
      });

      setPasswordSuccess(true);
      toast({
        title: "Password Changed",
        description: "Your password has been updated successfully.",
        variant: "success",
      });

      // Auto-close after a short delay
      setTimeout(() => {
        resetPasswordForm();
        setShowPasswordForm(false);
      }, 2000);
    } catch (err) {
      const message =
        (err as ApiClientError).message || "Network error. Please try again.";
      setPasswordError(message);
      setCurrentPassword("");
      console.error("Change password error:", err);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
              <UserCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle>My Profile</CardTitle>
              <CardDescription>
                Your account information and preferences
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Info Card */}
          <div className="flex items-start gap-4 p-4 rounded-xl border bg-muted/30">
            <Avatar className="h-16 w-16 text-lg">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {user.initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-1">
              <h3 className="text-lg font-semibold">{user.name}</h3>
              <div className="flex items-center gap-2">
                <Badge variant={roleColors[user.role]} className="gap-1">
                  {roleIcons[user.role]}
                  {roleLabels[user.role] || user.role}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground pt-1">
                {roleDescriptions[user.role] || "Standard access"}
              </p>
            </div>
          </div>

          {/* Details Grid */}
          {isEditingProfile ? (
            <form onSubmit={handleSaveProfile} className="space-y-4">
              {profileError && (
                <Alert variant="destructive">
                  <AlertDescription>{profileError}</AlertDescription>
                </Alert>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Full Name</Label>
                  <Input
                    id="profile-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={100}
                    required
                    disabled={isSavingProfile}
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-initials">Initials (Login ID)</Label>
                  <Input
                    id="profile-initials"
                    value={editInitials}
                    onChange={(e) =>
                      setEditInitials(e.target.value.toUpperCase())
                    }
                    maxLength={5}
                    required
                    disabled={isSavingProfile}
                    className="font-mono font-semibold uppercase"
                  />
                  <p className="text-xs text-muted-foreground">
                    2-5 uppercase letters. Used to log in and identify your
                    transactions.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelEditing}
                  disabled={isSavingProfile}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSavingProfile}>
                  {isSavingProfile ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Full Name
                  </p>
                  <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                    {user.name}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Initials (Login ID)
                  </p>
                  <p className="text-sm py-2 px-3 rounded-lg bg-muted font-mono font-semibold">
                    {user.initials}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Used to log in and identify your transactions
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handleStartEditing}
              >
                <Pencil className="h-4 w-4" />
                Edit Profile
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10">
              <Key className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <CardTitle>Security</CardTitle>
              <CardDescription>
                Manage your password and account security
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!showPasswordForm ? (
            <div className="flex items-center justify-between p-4 rounded-xl border bg-muted/30">
              <div className="space-y-1">
                <p className="text-sm font-medium">Password</p>
                <p className="text-sm text-muted-foreground">
                  Change your login password
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowPasswordForm(true)}
                className="gap-2"
              >
                <Key className="h-4 w-4" />
                Change Password
              </Button>
            </div>
          ) : (
            <div className="rounded-xl border p-4 space-y-4">
              {passwordSuccess ? (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 text-green-800">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">
                      Password changed successfully
                    </p>
                    <p className="text-xs text-green-600">
                      Your new password is now active.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold">Change Password</h3>
                    <p className="text-xs text-muted-foreground">
                      Enter your current password and choose a new one
                    </p>
                  </div>

                  {passwordError && (
                    <Alert variant="destructive">
                      <AlertDescription>{passwordError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      minLength={4}
                      maxLength={16}
                      required
                      disabled={isChangingPassword}
                      autoComplete="current-password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="settingsNewPassword">New Password</Label>
                    <Input
                      id="settingsNewPassword"
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      minLength={4}
                      maxLength={16}
                      required
                      disabled={isChangingPassword}
                      autoComplete="new-password"
                    />
                    <p className="text-xs text-muted-foreground">
                      Must be 4-16 characters and different from current
                      password
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="settingsConfirmPassword">
                      Confirm New Password
                    </Label>
                    <Input
                      id="settingsConfirmPassword"
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      minLength={4}
                      maxLength={16}
                      required
                      disabled={isChangingPassword}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancelPasswordChange}
                      disabled={isChangingPassword}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        isChangingPassword ||
                        currentPassword.length < 4 ||
                        newPassword.length < 4 ||
                        confirmPassword.length < 4
                      }
                    >
                      {isChangingPassword ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Changing...
                        </>
                      ) : (
                        "Update Password"
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Two-Factor Authentication */}
          <TwoFactorSection userInitials={user?.initials ?? ""} />
        </CardContent>
      </Card>
    </div>
  );
}
